import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getEventUsers, getSessionAnalytics } from '../../services/api';
import { useApiQuery } from '../../hooks/useApiQuery';


// ------------------------------------------------------
// Overview
// ------------------------------------------------------

const Overview = () => {
  const {
    eventId,
    event,
  } = useOutletContext();


  // ====================================================
  // Event Users
  // ====================================================

  const {
    data: usersData = [],
    isLoading: usersLoading,
    isError: usersError,
  } = useApiQuery(
    ['event-users', eventId],
    () =>
      getEventUsers(eventId)
        .then(res => res.data),
    {
      enabled: Boolean(eventId),

      staleTime:
        5 * 60 * 1000,

      onError: error => {
        console.error(
          'Failed to fetch event users:',
          error
        );
      },
    }
  );


  // ====================================================
  // Session Analytics
  // ====================================================
  //
  // IMPORTANT:
  // We no longer fetch ALL sessions here.
  //
  // Backend calculates:
  // - totalSessions
  // - activeSessions
  // - completedSessions
  // - avgDuration
  // - etc.
  //
  // ====================================================

  const {
    data: analyticsData = {},
    isLoading: analyticsLoading,
    isError: analyticsError,
  } = useApiQuery(
    [
      'session-analytics',
      eventId,
    ],
    () => getSessionAnalytics(eventId),
    {
      enabled:
        Boolean(eventId),

      staleTime:
        60 * 1000,

      onError: error => {
        console.error(
          'Failed to fetch dashboard analytics:',
          error
        );
      },
    }
  );


  // ====================================================
  // Summary
  // ====================================================

  const summary = useMemo(() => {

    const totalUsers =
      Array.isArray(usersData)
        ? usersData.length
        : 0;


    const totalScans =
      Number(
        analyticsData?.totalSessions
      ) || 0;


    const currentlyInside =
      Number(
        analyticsData?.activeSessions
      ) || 0;


    const completedSessions =
      Number(
        analyticsData?.completedSessions
      ) || 0;


    // Backend avgDuration is expected
    // to be in minutes based on the
    // existing analytics implementation.

    const avgTimeSpent =
      Number(
        analyticsData?.avgDuration
      ) || 0;


    // --------------------------------------------------
    // Present Today
    // --------------------------------------------------
    //
    // We use today's attendance trend
    // returned by the analytics endpoint
    // when available.
    //
    // --------------------------------------------------

    const today =
      new Date()
        .toISOString()
        .split('T')[0];


    const attendanceTrend =
      Array.isArray(
        analyticsData?.attendanceTrend
      )
        ? analyticsData.attendanceTrend
        : [];


    const todayData =
      attendanceTrend.find(
        item =>
          item.date === today
      );


    const presentToday =
      Number(
        todayData?.count
      ) || 0;


    return {
      totalUsers,

      presentToday,

      totalScans,

      avgTimeSpent:
        avgTimeSpent.toFixed(1),

      currentlyInside,

      completedSessions,
    };

  }, [
    usersData,
    analyticsData,
  ]);


  // ====================================================
  // Loading
  // ====================================================

  if (
    usersLoading ||
    analyticsLoading
  ) {
    return (
      <div className="flex items-center justify-center h-64">

        <div className="text-center">

          <div
            className="
              inline-block
              w-10
              h-10
              border-2
              border-[#1471b9]
              border-t-transparent
              rounded-full
              animate-spin
              mb-3
            "
          />

          <p className="text-[#708090] text-sm font-['Barlow']">
            Loading overview...
          </p>

        </div>

      </div>
    );
  }


  // ====================================================
  // Error
  // ====================================================

  if (
    usersError ||
    analyticsError
  ) {
    return (
      <div className="flex items-center justify-center h-64">

        <div className="text-center">

          <p className="text-red-500 text-lg font-['Barlow']">
            Failed to load overview data
          </p>

          <p className="text-[#708090] text-sm mt-2">
            Please refresh the page or try again later.
          </p>

        </div>

      </div>
    );
  }


  // ====================================================
  // No Data
  // ====================================================

  const hasData =
    summary.totalUsers > 0 ||
    summary.totalScans > 0;


  if (!hasData) {
    return (
      <div className="font-['Barlow']">

        <h2 className="text-2xl font-normal leading-9 text-[#2b3445] mb-6">
          Overview
        </h2>


        <div
          className="
            flex
            flex-col
            items-center
            justify-center
            py-16
            bg-white
            rounded-[15px]
            border
            border-[#dae1e7]
            shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)]
          "
        >

          <svg
            className="w-16 h-16 mb-4 text-[#dae1e7]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="
                M9 17V7
                m0 10a2 2 0 01-2 2H5
                a2 2 0 01-2-2V7
                a2 2 0 012-2h2
                a2 2 0 012 2
                m0 10a2 2 0 002 2h2
                a2 2 0 002-2
                M9 7a2 2 0 012-2h2
                a2 2 0 012 2
                m0 10V7
                m0 10a2 2 0 002 2h2
                a2 2 0 002-2V7
                a2 2 0 00-2-2h-2
                a2 2 0 00-2 2
              "
            />
          </svg>


          <p className="text-[#708090] text-sm">
            No data available for this event
          </p>

          <p className="text-xs text-[#708090] mt-1">
            Check back after attendees start checking in
          </p>

        </div>

      </div>
    );
  }


  // ====================================================
  // UI
  // ====================================================

  return (
    <div className="font-['Barlow']">

      <h2 className="text-2xl font-normal leading-9 text-[#2b3445] mb-6">
        Overview
      </h2>


      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

        <SummaryCard
          title="Total Users"
          value={
            summary.totalUsers
          }
          icon="👥"
        />


        <SummaryCard
          title="Present Today"
          value={
            summary.presentToday
          }
          icon="✅"
        />


        <SummaryCard
          title="Total Scans"
          value={
            summary.totalScans
          }
          icon="📱"
        />


        <SummaryCard
          title="Avg Time (min)"
          value={
            summary.avgTimeSpent
          }
          icon="⏱️"
        />


        <SummaryCard
          title="Currently Inside"
          value={
            summary.currentlyInside
          }
          icon="🚪"
        />

      </div>

    </div>
  );
};


// ======================================================
// Summary Card
// ======================================================

const SummaryCard = React.memo(
  ({
    title,
    value,
    icon,
  }) => {

    return (
      <div
        className="
          bg-white
          rounded-[15px]
          border
          border-[#dae1e7]
          shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)]
          hover:shadow-[0px_4px_16px_0px_rgba(43,52,69,0.1)]
          transition-shadow
          duration-200
          p-4
        "
      >

        <div className="flex items-center justify-between">

          <div>

            <p className="text-sm font-semibold text-[#708090] uppercase tracking-wide">
              {title}
            </p>

            <p className="text-[30px] font-normal leading-normal text-[#2b3445] mt-0.5">
              {value}
            </p>

          </div>


          <div className="text-3xl opacity-80">
            {icon}
          </div>

        </div>

      </div>
    );
  }
);


SummaryCard.displayName =
  'SummaryCard';


export default Overview;